import { useState } from 'react';
import { formatDate } from '../../../lib/format';
import { EXAM_TYPE_LABEL } from '@dru/types';
import { Link, useParams } from 'react-router-dom';
import type { AuditRow, CataloguePackageDetail, CatalogueTest, CursorPage } from '@dru/types';
import { INCLUSION_LABEL, TEST_CATEGORY_LABEL } from '@dru/types';
import { Alert, Button, Detail, DetailHeader, DetailSection, Dialog, KeyFigures, SearchInput, StatusBadge, TextField, TextSkeleton, cn } from '@dru/ui';
import { api } from '../../../lib/api';
import { fetchAll } from '../../../lib/fetchAll';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { CatalogueState, RetireAction, plural } from '../components/RetireAction';
import { ActivityTable, NotForYourRole } from './Audit';

import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';
import { groupTests } from '../../../lib/testGroups';

const TIER_TONE = { MANDATORY: 'covered', RECOMMENDED: 'pending', OPTIONAL: 'neutral' } as const;

/** One package: what it contains, who it is suggested to, and where it is sold. */
export function PackageDetail() {
  if (!useAuth().permissions.has('package.read')) {
    return <NotForYourRole title="Package" what="The clinical catalogue is maintained by platform administrators." />;
  }
  return <PackageDetailPage />;
}

function PackageDetailPage() {
  const { id = '' } = useParams();
  const pkg = useQuery<CataloguePackageDetail>(() => api.get(`/packages/${id}`), [id]);
  const trail = useQuery<CursorPage<AuditRow>>(() => api.get(`/audit?entityType=HealthPackage&entityId=${id}&limit=50`), [id]);
  const [editing, setEditing] = useState(false);
  const p = pkg.data;
  useBreadcrumbTitle(p?.name);
  const canManage = useAuth().permissions.has('package.manage');

  // Throws, so the confirmation stays open with the reason when refused.
  async function toggle() {
    if (!p) return;
    await api.patch(`/packages/${id}`, { isActive: !p.isActive });
    pkg.reload();
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow={p ? `Package · v${p.version}` : 'Package'}
        title={p?.name ?? (pkg.error ? 'Package not found' : 'Loading…')}
        badges={p && <CatalogueState active={p.isActive} />}
        subtitle={p && [p.code, p.category, p.description].filter(Boolean).join(' · ')}
        actions={p && canManage && (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>Edit tests</Button>
            {/* A default runs every examination of its kind; it is changed, never retired. */}
            {!p.defaultFor && <RetireAction
              variant="button"
              active={p.isActive}
              name={p.name}
              what="package"
              onConfirm={toggle}
              impact={p.isActive
                ? `Configured by ${plural(p.configurationCount, 'company', 'companies')}; suggested by ${plural(p.industries.length, 'industry', 'industries')} and ${plural(p.rules.length, 'rule')}.`
                : undefined}
              consequence="It stops being suggested at onboarding and cannot be added to a company’s configuration. Companies that already configured it should be told before you retire it."
            />}
          </>
        )}
      />
      {pkg.error && <Alert tone="danger">{pkg.error}</Alert>}
      {pkg.loading && !p && <TextSkeleton lines={6} />}
      {p && (
        <KeyFigures
          figures={[
            { key: 'tests', label: 'Tests', value: p.tests.length, hint: p.tests.some((t) => !t.isActive) ? `${p.tests.filter((t) => !t.isActive).length} retired` : `in ${groupTests(p.tests).length} groups`, tone: p.tests.some((t) => !t.isActive) ? 'dueSoon' : undefined },
            // A default serves every company; industry and rule suggestions
            // belong to the retired catalogue and would only read 0 here.
            ...(p.defaultFor
              ? [{ key: 'companies', label: 'Companies', value: 'All', hint: p.defaultFor === 'PRE_EMPLOYMENT' ? 'Every pre-employment examination' : 'Annual and all other examinations' }]
              : [
                { key: 'companies', label: 'Companies using it', value: p.configurationCount },
                { key: 'industries', label: 'Industries suggest it', value: p.industries.length },
                { key: 'rules', label: 'Rules suggest it', value: p.rules.length },
              ]),
          ]}
        />
      )}
      {p && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
          <DetailSection title={`Tests in it (${p.tests.length})`}>
            <div className="space-y-4">
              {groupTests(p.tests).map((g) => (
                <section key={g.key}>
                  <h3 className="mb-1 flex items-baseline gap-2 text-small font-semibold text-neutral-800">
                    {g.label} <span className="text-caption font-normal text-neutral-500">{g.tests.length}</span>
                  </h3>
                  <ul className="grid gap-x-4 rounded-input border border-neutral-100 px-3 py-1 sm:grid-cols-2">
                    {g.tests.map((t) => (
                      <li key={t.id} className="flex min-w-0 items-center gap-2 py-1 text-small" title={`${t.code} · ${TEST_CATEGORY_LABEL[t.category as never] ?? t.category}`}>
                        <Link to={`/admin/masters/tests/${t.id}`} className="inline-block min-w-0 py-1 text-primary-700 hover:underline">{t.name}</Link>
                        {!t.isActive && <StatusBadge tone="dueSoon">Retired test</StatusBadge>}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </DetailSection>

          <div className="space-y-4">
            <DetailSection title={`Industries that suggest it (${p.industries.length})`}>
              {p.industries.length === 0 ? <p className="text-small text-neutral-500">No industry lists it. Add it from an industry page.</p> : (
                <ul className="divide-y divide-neutral-100">
                  {p.industries.map((i) => (
                    <li key={i.id} className="flex items-center justify-between gap-2 py-1.5 text-small">
                      <Link to={`/admin/masters/industries/${i.id}`} className="inline-block py-1 text-primary-700 hover:underline">{i.name}</Link>
                      <StatusBadge tone={TIER_TONE[i.tier]}>{INCLUSION_LABEL[i.tier]}</StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </DetailSection>
            <DetailSection title="Record" phoneFolded>
              <div className="grid gap-3 sm:grid-cols-2">
                <Detail label="Version" value={`v${p.version} of ${p.versions.length}`} />
                <Detail label="Effective from" value={formatDate(p.effectiveFrom)} />
              </div>
            </DetailSection>
          </div>

          <DetailSection phoneFolded title={`Rules that suggest it (${p.rules.length})`} className="lg:col-span-2">
            {p.rules.length === 0 ? <p className="text-small text-neutral-500">No rule suggests this package.</p> : (
              <ul className="divide-y divide-neutral-100">
                {p.rules.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-small">
                    <span className="min-w-0 text-neutral-800">{r.name} <span className="text-caption text-neutral-500">· {r.industry?.name ?? 'any industry'} · {r.hazard?.name ?? 'any hazard'} · {r.examinationType ? (EXAM_TYPE_LABEL[r.examinationType] ?? r.examinationType) : 'any examination'} · priority {r.priority}</span></span>
                    <StatusBadge tone={TIER_TONE[r.tier]}>{INCLUSION_LABEL[r.tier]}</StatusBadge>
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          <DetailSection title="History" className="lg:col-span-2">
            <ActivityTable rows={trail.data?.items ?? []} loading={trail.loading} error={trail.error} onRetry={trail.reload}
              emptyHint="Changes to this package appear here." />
          </DetailSection>
        </div>
      )}
      {p && editing && <EditPackage pkg={p} onClose={() => setEditing(false)} onDone={() => { setEditing(false); pkg.reload(); }} />}
    </div>
  );
}

function EditPackage({ pkg, onClose, onDone }: { pkg: CataloguePackageDetail; onClose: () => void; onDone: () => void }) {
  const tests = useQuery<CatalogueTest[]>(
    () => fetchAll<CatalogueTest>((cursor) => api.get<CursorPage<CatalogueTest>>(`/tests?brief=true&limit=200${cursor ? `&cursor=${cursor}` : ''}`)), []);
  const [name, setName] = useState(pkg.name);
  const [description, setDescription] = useState(pkg.description ?? '');
  const [testIds, setTestIds] = useState<string[]>(pkg.tests.map((t) => t.id));
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const original = new Set(pkg.tests.map((t) => t.id));
  const added = testIds.filter((x) => !original.has(x)).length;
  const removed = [...original].filter((x) => !testIds.includes(x)).length;
  const term = search.trim().toLowerCase();
  const shown = (tests.data ?? []).filter((t) => !term || t.name.toLowerCase().includes(term) || t.code.toLowerCase().includes(term));

  async function save() {
    setBusy(true); setError(null);
    try { await api.patch(`/packages/${pkg.id}`, { name: name.trim(), description: description.trim(), testIds }); onDone(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog
      title={`Edit ${pkg.name}`}
      onClose={busy ? () => undefined : onClose}
      wide
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={name.trim().length < 2 || testIds.length === 0}>Save package</Button>
        </>
      )}
    >
      <div className="mt-3 space-y-4">
        {/* The contents are changed in place, not versioned — so the people
            it lands on are named before the save, not discovered after. */}
        {pkg.configurationCount > 0 && (
          <Alert tone="warning" title={`${plural(pkg.configurationCount, 'company', 'companies')} use this package`}>
            A change to its tests applies to their next examinations. Examinations already raised keep the tests they were raised with.
          </Alert>
        )}
        <TextField label="Name" required value={name} onChange={setName} autoFocus />
        <TextField label="Description (optional)" value={description} onChange={setDescription} />
        <fieldset>
          <legend className="flex w-full flex-wrap items-baseline justify-between gap-2 text-caption text-neutral-700">
            <span>Tests</span>
            <span className="text-neutral-500">
              {testIds.length} chosen{added || removed ? ` · ${added} added, ${removed} removed` : ''}
            </span>
          </legend>
          <SearchInput value={search} onValueChange={setSearch} placeholder="Find a test" aria-label="Find a test" className="mt-1.5" />
          <div className="mt-2 max-h-64 overflow-y-auto rounded-input border border-neutral-200 p-1">
            {tests.loading && <p className="p-2 text-small text-neutral-500">Loading tests…</p>}
            {!tests.loading && shown.length === 0 && <p className="p-2 text-small text-neutral-500">No test matches “{search}”.</p>}
            {shown.map((t) => {
              const on = testIds.includes(t.id);
              return (
                <label key={t.id} className={cn('flex min-h-[32px] max-md:min-h-10 cursor-pointer items-center gap-2 rounded px-2 py-1 text-small hover:bg-neutral-50', on && 'bg-primary-50')}>
                  <input type="checkbox" className="h-4 w-4 shrink-0" checked={on} onChange={() => setTestIds(on ? testIds.filter((x) => x !== t.id) : [...testIds, t.id])} />
                  <span className="min-w-0 flex-1 truncate text-neutral-800">{t.name}</span>
                  <span className="font-mono text-caption text-neutral-500">{t.code}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}
